// styles/GlobalThemeStyle.js
import { createGlobalStyle } from "styled-components";

/**
 * Tokens semânticos da plataforma.
 * Componentes devem consumir estes tokens em vez de cores literais.
 * As cores de marca permanecem disponíveis em --brand-*.
 */
export const GlobalThemeStyle = createGlobalStyle`
  :root,
  :root[data-theme="dark"] {
    --brand-navy: #00212d;
    --brand-blue: #006693;
    --brand-green: #00b056;
    --brand-lime: #dbfb8e;

    --app-bg: #00212d;
    --app-bg-secondary: #006693;
    --app-bg-gradient: linear-gradient(
      135deg,
      #00212d 0%,
      #006693 50%,
      #00212d 100%
    );
    --sidebar-bg: linear-gradient(
      135deg,
      #00212d 0%,
      #006693 50%,
      #00b056 100%
    );

    --surface-1: #07303d;
    --surface-2: #0c3b49;
    --surface-3: #124a58;
    --surface-glass: rgba(255, 255, 255, 0.08);
    --surface-glass-strong: rgba(255, 255, 255, 0.13);
    --surface-overlay: rgba(0, 33, 45, 0.94);
    
    --surface-primary: var(--surface-1);
    --surface-secondary: var(--surface-2);
    --surface-hover: rgba(219, 251, 142, 0.12);
    --surface-highlight: rgba(219, 251, 142, 0.06);

    --text-primary: #f5fbff;
    --text-secondary: rgba(245, 251, 255, 0.78);
    --text-muted: rgba(245, 251, 255, 0.58);
    --text-heading: #dbfb8e;
    --text-on-accent: #00212d;
    --text-on-secondary: var(--text-on-accent);
    --text-on-lime: #00212d;

    --border-subtle: rgba(219, 251, 142, 0.18);
    --border-default: rgba(219, 251, 142, 0.32);
    --border-strong: rgba(219, 251, 142, 0.62);

    --accent: #00b056;
    --accent-hover: #00c766;
    --accent-soft: rgba(0, 176, 86, 0.18);
    --accent-secondary: #dbfb8e;
    --accent-secondary-soft: rgba(219, 251, 142, 0.14);
    --accent-secondary-hover: #009647;

    --success: #25d07f;
    --success-soft: rgba(37, 208, 127, 0.18);
    --warning: #f5b942;
    --danger: #ff6b6b;
    --danger-soft: rgba(255, 107, 107, 0.18);
    --text-on-danger: #ffffff;
    --info: #4ed8ff;

    --color-secondary-1: var(--accent-secondary);
    --color-primary-2: var(--accent);

    --brand-cta-gradient: linear-gradient(45deg, #00b056, #006693);
    --brand-cta-gradient-hover: linear-gradient(45deg, #006693, #00b056);
    --brand-cta-gradient-135: linear-gradient(135deg, #00b056 0%, #006693 100%);
    --text-on-brand: #ffffff;
    --progress-bar-gradient: linear-gradient(90deg, #00c6ff 0%, #0072ff 100%);

    --sidebar-icon-filter: none;
    --sidebar-logo-filter: none;

    --shadow-sm: 0 3px 10px rgba(0, 0, 0, 0.24);
    --shadow-md: 0 8px 28px rgba(0, 0, 0, 0.32);
    --shadow-lg: 0 18px 55px rgba(0, 0, 0, 0.42);

    --title-shadow: 0 4px 20px rgba(219, 251, 142, 0.15);
    --wrapper-overlay-opacity: 0.3;

    --input-bg: rgba(255, 255, 255, 0.1);
    --input-bg-focus: rgba(255, 255, 255, 0.15);
    --input-placeholder: rgba(245, 251, 255, 0.48);
    --table-row-hover: rgba(219, 251, 142, 0.08);

    --scrollbar-track: rgba(0, 33, 45, 0.35);
    --scrollbar-thumb: #00b056;
  }

  :root[data-theme="light"] {
    --app-bg: #eef5f3;
    --app-bg-secondary: #dbece8;
    --app-bg-gradient: linear-gradient(
      135deg,
      #f7fbfa 0%,
      #e4f1ee 52%,
      #d6e9e4 100%
    );
    --sidebar-bg: linear-gradient(
      160deg,
      #f7fbfa 0%,
      #dceee9 54%,
      #bce5d1 100%
    );

    --surface-1: #ffffff;
    --surface-2: #f4f9f7;
    --surface-3: #e9f3f0;
    --surface-glass: rgba(255, 255, 255, 0.76);
    --surface-glass-strong: rgba(255, 255, 255, 0.92);
    --surface-overlay: rgba(255, 255, 255, 0.96);
    
    --surface-primary: var(--surface-1);
    --surface-secondary: var(--surface-2);
    --surface-hover: rgba(0, 140, 74, 0.08);
    --surface-highlight: rgba(0, 140, 74, 0.04);

    --text-primary: #16343c;
    --text-secondary: #3d5960;
    --text-muted: #6d858a;
    --text-heading: #075d3a;
    /* Texto claro em botões verdes/CTA (accent e accent-secondary são verdes sólidos no light) */
    --text-on-accent: #ffffff;
    --text-on-secondary: #ffffff;
    /* Texto escuro sobre --brand-lime (#dbfb8e) */
    --text-on-lime: #00212d;

    --border-subtle: rgba(0, 75, 62, 0.12);
    --border-default: rgba(0, 96, 72, 0.26);
    --border-strong: rgba(0, 112, 75, 0.48);

    --accent: #008c4a;
    --accent-hover: #00783f;
    --accent-soft: rgba(0, 140, 74, 0.12);
    --accent-secondary: #16734b;
    --accent-secondary-soft: rgba(22, 115, 75, 0.1);
    --accent-secondary-hover: #0d5f3e;

    --success: #087f4d;
    --success-soft: rgba(8, 127, 77, 0.18);
    --warning: #9a6500;
    --danger: #c93636;
    --danger-soft: rgba(201, 54, 54, 0.14);
    --text-on-danger: #ffffff;
    --info: #087fa3;

    --color-secondary-1: var(--accent-secondary);
    --color-primary-2: var(--accent);

    --brand-cta-gradient: linear-gradient(45deg, #008c4a, #0a6b8a);
    --brand-cta-gradient-hover: linear-gradient(45deg, #0a6b8a, #008c4a);
    --brand-cta-gradient-135: linear-gradient(135deg, #008c4a 0%, #0a6b8a 100%);
    --text-on-brand: #ffffff;
    --progress-bar-gradient: linear-gradient(90deg, #00a8d8 0%, #0a5fd4 100%);

    --sidebar-icon-filter: brightness(0) saturate(100%) invert(18%) sepia(24%)
      saturate(1200%) hue-rotate(142deg) brightness(92%) contrast(95%);
    --sidebar-logo-filter: none;

    --shadow-sm: 0 3px 10px rgba(14, 55, 48, 0.1);
    --shadow-md: 0 8px 28px rgba(14, 55, 48, 0.14);
    --shadow-lg: 0 18px 55px rgba(14, 55, 48, 0.18);

    --title-shadow: 0 4px 20px rgba(22, 115, 75, 0.12);
    --wrapper-overlay-opacity: 0.15;

    --input-bg: rgba(255, 255, 255, 0.84);
    --input-bg-focus: #ffffff;
    --input-placeholder: #789096;
    --table-row-hover: rgba(0, 140, 74, 0.08);

    --scrollbar-track: rgba(0, 75, 62, 0.1);
    --scrollbar-thumb: #58a987;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html,
  body,
  #root {
    min-height: 100%;
  }

  body {
    background: var(--app-bg);
    color: var(--text-primary);
    transition: background-color 180ms ease, color 180ms ease;
  }

  @media screen and (max-width: 768px) {
    html,
    body {
      overflow-x: hidden;
      max-width: 100vw;
    }

    #root {
      overflow-x: hidden;
      max-width: 100vw;
    }

    input,
    select,
    textarea,
    button {
      font-size: 16px; /* evita zoom automático no iOS */
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--text-heading);
  }

  p,
  label {
    color: var(--text-secondary);
  }

  input,
  textarea,
  select {
    color-scheme: inherit;
  }

  ::selection {
    color: var(--text-on-accent);
    background: var(--accent-secondary);
  }

  * {
    scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  }

  /* ─── SweetAlert2 — identidade VERT ─── */
  .swal2-container.swal2-vert-container {
    backdrop-filter: blur(6px);
  }

  .swal2-container.swal2-vert-container.swal2-backdrop-show {
    background: color-mix(in srgb, var(--brand-navy, #00212d) 52%, transparent) !important;
  }

  .swal2-container.swal2-vert-container--light.swal2-backdrop-show {
    background: rgba(14, 55, 48, 0.38) !important;
  }

  .swal2-popup.swal2-vert-popup,
  .swal2-popup {
    background: var(--surface-1) !important;
    color: var(--text-primary) !important;
    border: 1px solid var(--border-subtle) !important;
    border-radius: 18px !important;
    box-shadow: var(--shadow-lg) !important;
    padding: 1.75rem 1.5rem 1.5rem !important;
    width: min(100%, 28rem) !important;
    overflow: hidden;
    position: relative;
  }

  .swal2-popup.swal2-vert-popup::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient-135);
  }

  /* Força paleta light (telas públicas / fora do app) */
  .swal2-popup.swal2-vert-popup--light {
    --surface-1: #ffffff;
    --surface-2: #f4f9f7;
    --surface-3: #e9f3f0;
    --text-primary: #16343c;
    --text-secondary: #3d5960;
    --text-muted: #6d858a;
    --text-heading: #075d3a;
    --text-on-brand: #ffffff;
    --text-on-accent: #ffffff;
    --border-subtle: rgba(0, 75, 62, 0.12);
    --border-default: rgba(0, 96, 72, 0.26);
    --accent: #008c4a;
    --accent-hover: #00783f;
    --accent-soft: rgba(0, 140, 74, 0.12);
    --accent-secondary: #16734b;
    --success: #087f4d;
    --warning: #9a6500;
    --danger: #c93636;
    --info: #087fa3;
    --input-bg: #f4f9f7;
    --input-bg-focus: #ffffff;
    --shadow-lg: 0 18px 55px rgba(14, 55, 48, 0.18);
    --brand-cta-gradient-135: linear-gradient(135deg, #008c4a 0%, #0a6b8a 100%);
  }

  .swal2-title.swal2-vert-title,
  .swal2-title {
    color: var(--text-heading) !important;
    font-size: 1.35rem !important;
    font-weight: 700 !important;
    line-height: 1.3 !important;
    padding: 0.35rem 0 0 !important;
  }

  .swal2-html-container.swal2-vert-html,
  .swal2-html-container,
  .swal2-content {
    color: var(--text-secondary) !important;
    font-size: 0.95rem !important;
    line-height: 1.55 !important;
  }

  .swal2-icon.swal2-vert-icon,
  .swal2-icon {
    margin: 0.5rem auto 0.75rem !important;
    border-width: 3px !important;
  }

  .swal2-icon.swal2-success {
    border-color: color-mix(in srgb, var(--success) 45%, transparent) !important;
    color: var(--success) !important;
  }

  .swal2-icon.swal2-success .swal2-success-ring {
    border-color: color-mix(in srgb, var(--success) 35%, transparent) !important;
  }

  .swal2-icon.swal2-success [class^="swal2-success-line"] {
    background-color: var(--success) !important;
  }

  .swal2-icon.swal2-error {
    border-color: color-mix(in srgb, var(--danger) 45%, transparent) !important;
    color: var(--danger) !important;
  }

  .swal2-icon.swal2-error [class^="swal2-x-mark-line"] {
    background-color: var(--danger) !important;
  }

  .swal2-icon.swal2-warning {
    border-color: color-mix(in srgb, var(--warning) 45%, transparent) !important;
    color: var(--warning) !important;
  }

  .swal2-icon.swal2-info {
    border-color: color-mix(in srgb, var(--info) 45%, transparent) !important;
    color: var(--info) !important;
  }

  .swal2-icon.swal2-question {
    border-color: color-mix(in srgb, var(--accent) 45%, transparent) !important;
    color: var(--accent) !important;
  }

  .swal2-actions.swal2-vert-actions {
    gap: 0.65rem !important;
    margin: 1.25rem auto 0 !important;
  }

  .swal2-styled.swal2-confirm,
  .swal2-vert-confirm {
    background: var(--accent) !important;
    background-image: none !important;
    color: var(--text-on-brand) !important;
    border: none !important;
    border-radius: 999px !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    padding: 0.7rem 1.85rem !important;
    box-shadow: var(--shadow-sm) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease !important;
  }

  .swal2-styled.swal2-confirm:hover,
  .swal2-vert-confirm:hover {
    background: var(--accent-hover) !important;
    transform: translateY(-1px);
  }

  .swal2-styled.swal2-confirm.swal2-vert-confirm--danger,
  .swal2-vert-confirm--danger {
    background: var(--danger) !important;
  }

  .swal2-styled.swal2-confirm.swal2-vert-confirm--danger:hover,
  .swal2-vert-confirm--danger:hover {
    filter: brightness(0.92);
    background: var(--danger) !important;
  }

  .swal2-styled.swal2-cancel,
  .swal2-vert-cancel,
  .swal2-styled.swal2-deny,
  .swal2-vert-deny {
    background: var(--surface-3) !important;
    background-image: none !important;
    color: var(--text-primary) !important;
    border: 1px solid var(--border-default) !important;
    border-radius: 999px !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    padding: 0.7rem 1.85rem !important;
    box-shadow: none !important;
  }

  .swal2-styled.swal2-deny,
  .swal2-vert-deny {
    background: var(--danger) !important;
    color: var(--text-on-brand) !important;
    border-color: var(--danger) !important;
  }

  .swal2-styled.swal2-confirm:focus,
  .swal2-styled.swal2-cancel:focus,
  .swal2-styled.swal2-deny:focus {
    box-shadow: 0 0 0 3px var(--accent-soft) !important;
  }

  .swal2-input,
  .swal2-textarea,
  .swal2-select,
  .swal2-vert-input {
    background: var(--input-bg) !important;
    color: var(--text-primary) !important;
    border: 2px solid var(--border-default) !important;
    border-radius: 12px !important;
    box-shadow: none !important;
  }

  .swal2-input:focus,
  .swal2-textarea:focus,
  .swal2-select:focus {
    border-color: var(--accent) !important;
    box-shadow: 0 0 0 3px var(--accent-soft) !important;
  }

  .swal2-validation-message {
    background: var(--danger-soft, rgba(201, 54, 54, 0.14)) !important;
    color: var(--danger) !important;
    border-radius: 10px !important;
  }

  .swal2-timer-progress-bar {
    background: var(--accent) !important;
  }
`;